import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BRANDS, CHANNELS, CONTENT_TYPES } from '@hub/shared'
import type { BrandId, Channel, ContentType } from '@hub/shared'
import { addDays, monthEnd, monthStart, today, weekStart } from './dates.js'
import type { ItemQuery } from './items.js'

/**
 * Filters live in the address bar, so they survive switching between
 * pipeline, calendar and tasks — and a filtered view can be bookmarked.
 */

export const PERIODS = [
  { id: 'alle', label: 'Alle' },
  { id: 'woche', label: 'Diese Woche' },
  { id: '4wochen', label: 'Nächste 4 Wochen' },
  { id: 'monat', label: 'Dieser Monat' },
] as const
export type Period = (typeof PERIODS)[number]['id']

export interface Filters {
  brand: BrandId[]
  channel: Channel[]
  type: ContentType[]
  period: Period
  q: string
}

function pick<T extends string>(raw: string | null, allowed: readonly T[]): T[] {
  if (!raw) return []
  return raw.split(',').filter((v): v is T => (allowed as readonly string[]).includes(v))
}

export function periodRange(period: Period): { from?: string; to?: string } {
  const t = today()
  switch (period) {
    case 'woche': {
      const start = weekStart(t)
      return { from: start, to: addDays(start, 6) }
    }
    case '4wochen':
      return { from: t, to: addDays(t, 27) }
    case 'monat':
      return { from: monthStart(t), to: monthEnd(t) }
    default:
      return {}
  }
}

export function useFilters() {
  const [params, setParams] = useSearchParams()

  const filters = useMemo<Filters>(() => {
    const period = params.get('zeit')
    return {
      brand: pick(params.get('marke'), BRANDS),
      channel: pick(params.get('kanal'), CHANNELS),
      type: pick(params.get('art'), CONTENT_TYPES),
      period: PERIODS.some((p) => p.id === period) ? (period as Period) : 'alle',
      q: params.get('q') ?? '',
    }
  }, [params])

  const update = useCallback(
    (patch: Partial<Filters>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          const set = (key: string, value: string): void => {
            if (value) next.set(key, value)
            else next.delete(key)
          }
          if (patch.brand) set('marke', patch.brand.join(','))
          if (patch.channel) set('kanal', patch.channel.join(','))
          if (patch.type) set('art', patch.type.join(','))
          if (patch.period) set('zeit', patch.period === 'alle' ? '' : patch.period)
          if (patch.q !== undefined) set('q', patch.q)
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const reset = useCallback(() => update({ brand: [], channel: [], type: [], period: 'alle', q: '' }), [update])

  const active =
    filters.brand.length + filters.channel.length + filters.type.length + (filters.period === 'alle' ? 0 : 1) + (filters.q ? 1 : 0)

  return { filters, update, reset, active }
}

/** The API query for a filter set. `withPeriod: false` for the calendar, which pages by month itself. */
export function toQuery(f: Filters, withPeriod = true): ItemQuery {
  return {
    brand: f.brand,
    channel: f.channel,
    type: f.type,
    ...(f.q ? { q: f.q } : {}),
    ...(withPeriod ? periodRange(f.period) : {}),
  }
}

/** Opening an item or the "new" sheet is part of the address too: `?eintrag=<id>`, `?neu=1`. */
export function useSheetParam() {
  const [params, setParams] = useSearchParams()
  const open = useCallback(
    (key: 'eintrag' | 'neu', value: string | undefined, idea?: string) => {
      setParams((current) => {
        const next = new URLSearchParams(current)
        next.delete('eintrag')
        next.delete('neu')
        next.delete('idee')
        if (value) next.set(key, value)
        if (idea) next.set('idee', idea)
        return next
      })
    },
    [setParams],
  )
  return {
    itemId: params.get('eintrag') ?? undefined,
    creating: params.get('neu') !== null,
    newDate: params.get('neu') && params.get('neu') !== '1' ? (params.get('neu') ?? undefined) : undefined,
    openItem: (id: string) => open('eintrag', id),
    fromIdea: params.get('idee') ?? undefined,
    openNew: (date?: string) => open('neu', date ?? '1'),
    /** New entry prefilled from an inspiration find. */
    openFromIdea: (ideaId: string, date?: string) => open('neu', date ?? '1', ideaId),
    close: () => open('eintrag', undefined),
  }
}
